import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { sek, INSTALLED_BY } from '@/lib/quoteCalc';

const Row = ({ label, value, className = '' }) => (
  <div className={`flex justify-between ${className}`}><span>{label}</span><span>{value}</span></div>
);

export function QuoteTotalsSummary({ t, testId }) {
  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 p-4 space-y-1.5 text-sm">
        <Row label="Hårdvara" value={sek(t.hw)} />
        {t.discAmt > 0 && <Row label={`Rabatt hårdvara (${t.disc}%)`} value={`-${sek(t.discAmt)}`} className="text-red-600" />}
        {t.acc > 0 && <Row label="Tillbehör" value={sek(t.acc)} className="" />}
        {t.install > 0 && <Row label="Installation" value={sek(t.install)} />}
        <Row label="Vapelarm Cloud – licens första året" value={sek(t.lic)} />
        <hr className="border-slate-300 dark:border-slate-600" />
        <Row label="Totalt exklusive moms" value={sek(t.sumEx)} className="font-semibold" />
        <Row label="Moms 25 %" value={sek(t.vat)} className="text-muted-foreground" />
        <div className="flex justify-between items-end pt-2 border-t border-slate-300 dark:border-slate-600">
          <span><span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Totalt första avtalsåret</span><span className="font-bold">Totalt inklusive moms</span></span>
          <span className="text-lg font-extrabold" data-testid={`${testId}-total`}>{sek(t.sumInkl)}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg border p-2.5"><div className="text-muted-foreground">Engångskostnad</div><div className="font-bold text-sm" data-testid={`${testId}-onetime`}>{sek(t.oneTime)}</div></div>
        <div className="rounded-lg border p-2.5"><div className="text-muted-foreground">Återkommande licens</div><div className="font-bold text-sm" data-testid={`${testId}-recurring`}>{sek(t.recurring)}/år</div></div>
      </div>
    </div>
  );
}

const fmt = (d) => (d ? new Date(d).toLocaleString('sv-SE') : '–');

export function SigningLogDialog({ log, onClose }) {
  return (
    <Dialog open={!!log} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="signing-log-dialog">
        <DialogHeader><DialogTitle>Signeringslogg</DialogTitle></DialogHeader>
        {log && log.length === 0 && <p className="text-sm text-muted-foreground">Ingen signeringslogg finns. Offerten signerades innan loggen infördes.</p>}
        {log?.map(e => (
          <div key={e.id} className="rounded-lg border p-3 text-xs space-y-1 font-mono break-all">
            {[
              ['Signerad av', `${e.signed_by} (${e.signer_title})${e.signer_email ? ` · ${e.signer_email}` : ''}`], ['Organisation', `${e.organisation || ''} ${e.org_number || ''}`],
              ['Tidpunkt', fmt(e.signed_at)], ['Offert', `${e.quote_number} · version ${e.quote_version}`],
              ['Belopp', `${sek(e.sum_ex_vat)} exkl. / ${sek(e.sum_inkl_vat)} inkl. moms`],
              ['Behörighet bekräftad', e.authority_confirmed ? 'Ja' : 'Nej'], ['Villkor godkända', e.terms_accepted ? 'Ja' : 'Nej'],
              ['Innehålls-hash', e.content_hash], ['Villkors-hash', e.terms_hash], ['Signatur-hash', e.signature_hash],
              ['IP', e.ip], ['Webbläsare', e.user_agent],
            ].map(([k, v]) => <div key={k}><span className="text-muted-foreground font-sans">{k}: </span>{v}</div>)}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export function PreviewExtras({ f }) {
  const inc = f.installation_included;
  const rows = [
    ['Beräknad leveranstid', f.delivery_time],
    ['Installation ingår', inc === true ? 'Ja' : inc === false ? 'Nej – ingår inte i offerten' : ''],
    ['Installation utförs av', INSTALLED_BY[f.installation_by]],
    ['Tekniska förutsättningar', f.technical_requirements],
    ['WiFi-krav', f.wifi_required ? (f.wifi_requirements || 'Tillgängligt och kompatibelt WiFi-nätverk') : ''],
    ['Övriga kommentarer', f.delivery_comments],
    ['Fakturareferens', f.invoice_reference], ['Fakturaadress', f.invoice_address],
    ['Inköpsordernummer', f.po_number], ['Betalningsvillkor', f.payment_terms],
  ].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <div className="rounded-lg border p-3 text-xs space-y-1" data-testid="quote-preview-extras">
      <div className="font-semibold mb-1">Leverans, installation och fakturering</div>
      {rows.map(([k, v]) => <div key={k} className="flex gap-2"><span className="text-muted-foreground w-40 shrink-0">{k}</span><span className="whitespace-pre-line">{v}</span></div>)}
    </div>
  );
}
